import {
	AccountCircle,
	BusinessRounded,
	KeyboardArrowDown,
	KeyboardArrowRight,
	PostAdd,
	QuestionAnswerRounded,
	Settings,
	StorageRounded,
} from "@mui/icons-material";
import {
	Box,
	Collapse,
	CSSObject,
	Divider,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	styled,
	Theme,
	Toolbar,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import MuiDrawer from "@mui/material/Drawer";
import { SIDEBAR_WIDTH } from "../../sidebar";
import ChatContent from "components/ChatContent";
import { useTranslation } from "react-i18next";
import useIndexedDB from "hooks/useIndexedDB";
import React from "react";
import { getAllLastMessages } from "services/api";
import { Channel } from "types/channel";
import { Account, AccountContext } from "services/account";
import Item from "./components/Item";
import Items from "./components/Items";
import Logo from "components/Logo";
const openedMixin = (theme: Theme): CSSObject => ({
	width: SIDEBAR_WIDTH,
	transition: theme.transitions.create("width", {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.enteringScreen,
	}),
	overflowX: "hidden",
	zIndex: theme.zIndex.drawer - 1,
	textWrap: "wrap",
});

const closedMixin = (theme: Theme): CSSObject => ({
	transition: theme.transitions.create("width", {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.leavingScreen,
	}),
	overflowX: "hidden",
	width: `calc(${theme.spacing(7)} + 1px)`,
	[theme.breakpoints.up("sm")]: {
		width: `calc(${theme.spacing(8)} + 1px)`,
	},
	zIndex: theme.zIndex.drawer - 1,
	textWrap: "nowrap",
});

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== "open" })(({ theme }) => ({
	width: SIDEBAR_WIDTH,
	flexShrink: 0,
	whiteSpace: "nowrap",
	boxSizing: "border-box",

	variants: [
		{
			props: { open: true },
			style: {
				...openedMixin(theme),
				"& .MuiDrawer-paper": openedMixin(theme),
			},
		},
		{
			props: { open: false },
			style: {
				...closedMixin(theme),
				"& .MuiDrawer-paper": closedMixin(theme),
			},
		},
	],
}));

interface MenuItem {
	item: JSX.Element;
	hidden?: boolean;
}

const Sidebar = ({ open, setOpen }) => {
	const { t } = useTranslation(["common"]);
	const theme = useTheme();
	const account = React.useContext(AccountContext);
	const isSm = useMediaQuery(theme.breakpoints.down("sm"));
	const [channels, setChannels] = React.useState<Channel[]>(JSON.parse(localStorage.getItem("sidebarChannels") || "[]"));

	React.useEffect(() => {
		getAllLastMessages().then((resp) => {
			if (!resp || resp?.error) return;
			let messages=[];
			if (resp.messages){
				messages=resp.messages;

			}
			localStorage.setItem("sidebarChannels", JSON.stringify(messages));
			setChannels(messages);
		});
	}, []);

	const handleDrawerClose = () => {
		setOpen(false);
	};
	const container = window !== undefined ? () => window.document.body : undefined;

	const menuItem: MenuItem[] = [
		{
			item: (
				<Item
					key={"allChannels"}
					link="/app/chats"
					text={t("allChannels")}
					icon={<QuestionAnswerRounded />}
				/>
			),
		},
		{
			item: (
				<Item
					key={"allDatasets"}
					link="/app/dataset/list"
					text={t("allDatasets")}
					icon={<StorageRounded />}
				/>
			),
			hidden: ["organizationUser"].includes(account.getRole()),
		},
		{
			item: (
				<Item
					key={"organizationManagement"}
					link="/app/organization/List"
					text={t("organizationManagement")}
					icon={<BusinessRounded />}
				/>
				// <Items
				// 	key={"organizationManagement"}
				// 	open={open}
				// 	items={[
				// 		<Item
				// 			key={"allOrganization"}
				// 			link="/app/organization/List"
				// 			text={t("allOrganization")}
				// 			icon={<BusinessRounded />}
				// 		/>,
				// 	]}
				// 	text={t("organizationManagement")}
				// 	icon={<BusinessRounded />}
				// />
			),
			hidden: !["organizationAdmin"].includes(account.getRole()),
		},
		{
			item: (
				<Item
					key={"setting"}
					link="/app/setting"
					text={t("setting")}
					icon={<Settings />}
				/>
			),
		},
	];

	const drawer = (
		<>
			<Toolbar>
				<Box
					component="a"
					href="/app/"
					display={{ xs: "block", sm: "none" }}>
					<Logo />
				</Box>
			</Toolbar>
			<Box sx={{ overflow: "auto" }}>
				<Divider />
				{/* Add your content here */}
				<List sx={{ overflow: "hidden" }}>
					<Collapse in={(isSm || open) && channels.length > 0}>
						{channels.length > 0 &&
							channels.slice(0, 5).map((channel) => {
								return (
									<React.Fragment key={channel.channel}>
										<ListItem
											disablePadding
											sx={{ display: "block" }}>
											<Box p="8px 20px">
												<Box
													component="a"
													href={"/app/chat/" + channel.channel}
													sx={{ color: "inherit" }}>
													<ChatContent channel={channel} />
												</Box>
											</Box>
										</ListItem>
										<Divider component="li" />
									</React.Fragment>
								);
							})}
					</Collapse>

					{menuItem.map((item, i) => {
						if (item.hidden) return null;

						let output = [item.item];
						if (i < menuItem.length - 1) {
							output.push(
								<Divider
									key={i}
									component="li"
								/>
							);
						}

						return output;
					})}
				</List>
			</Box>
		</>
	);

	return (
		<Box
			component="nav"
			sx={{ flexShrink: { sm: 0 } }}>
			<MuiDrawer
				keepMounted
				container={container}
				variant="temporary"
				open={open}
				onClose={handleDrawerClose}
				ModalProps={{
					keepMounted: true, // Better open performance on mobile.
				}}
				sx={{
					display: { xs: "block", sm: "none" },
					"& .MuiDrawer-paper": { boxSizing: "border-box", width: SIDEBAR_WIDTH },
				}}>
				{drawer}
			</MuiDrawer>
			<Drawer
				keepMounted
				variant={"permanent"}
				open={open}
				sx={{
					display: { xs: "none", sm: "block" },
				}}>
				{drawer}
			</Drawer>
		</Box>
	);
};

export default Sidebar;
